import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Box,
} from "@mui/material";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import { tokens } from "@/tokens";
import { StatusBadge } from "./status-badge";
import { formatCurrency } from "../utils";

interface CasesTableProps {
  cases: any[];
  isLoading: boolean;
  onRowClick: (fsCase: any) => void;
}

const formatValue = (value: number | null | undefined) => {
  if (value == null) return "—";
  return formatCurrency(value);
};

export const CasesTable: React.FC<CasesTableProps> = ({
  cases,
  isLoading,
  onRowClick,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ ...tableContainerSx, ...tableCompactSx }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Case #</TableCell>
                <TableCell>PO</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Middleman Value</TableCell>
                <TableCell align="right">Factory Value</TableCell>
                <TableCell align="right">Savings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (!cases.length) {
    return (
      <Box sx={{ ...tableContainerSx, ...tableCompactSx, py: 6, textAlign: "center" }}>
        <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2 }}>
          No cases found. Create a case to start the first sale process.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...tableContainerSx, ...tableCompactSx }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Case #</TableCell>
              <TableCell>PO</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Middleman Value</TableCell>
              <TableCell align="right">Factory Value</TableCell>
              <TableCell align="right">Savings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map((fsCase: any) => (
              <TableRow
                key={fsCase._id}
                onClick={() => onRowClick(fsCase)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Typography
                    sx={{
                      fontWeight: tokens.fontWeight.semibold,
                      fontSize: tokens.fontSize.sm2,
                      color: tokens.text.primary,
                    }}
                  >
                    {fsCase.caseNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: tokens.fontSize.sm2,
                      color: tokens.text.body,
                    }}
                  >
                    {fsCase.poNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusBadge status={fsCase.status} />
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>
                    {formatValue(fsCase.middlemanValue)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>
                    {formatValue(fsCase.factoryValue)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const s = fsCase.middlemanValue && fsCase.factoryValue && fsCase.dutyRate
                      ? (fsCase.middlemanValue - fsCase.factoryValue) * (fsCase.dutyRate / 100)
                      : null;
                    return (
                      <Typography
                        sx={{
                          fontSize: tokens.fontSize.sm2,
                          fontWeight: tokens.fontWeight.bold,
                          color: s ? tokens.status.success : tokens.text.muted,
                        }}
                      >
                        {s ? formatValue(s) : "\u2014"}
                      </Typography>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
